import { MecImage } from '@adapters/bigcommerce/types/product';

const TRANSPARENT_SUFFIX = '_transparent';
const TRAILING_ASSET_ID_PATTERN = /__(\d+(?:\.\d+)*)$/;

export function extractCanonicalFilenameStem(urlOriginal: string): string {
  const [pathOnly] = urlOriginal.split(/[?#]/, 1);
  const lastSegment = pathOnly.split('/').pop() ?? pathOnly;
  const lastDotIndex = lastSegment.lastIndexOf('.');
  const stem =
    lastDotIndex > -1 ? lastSegment.slice(0, lastDotIndex) : lastSegment;

  return stem
    .replace(TRAILING_ASSET_ID_PATTERN, '')
    .replace(new RegExp(`${TRANSPARENT_SUFFIX}(?=__|$)`, 'i'), '');
}

export function isTransparentVariant(urlOriginal: string): boolean {
  const [pathOnly] = urlOriginal.split(/[?#]/, 1);
  const lastSegment = pathOnly.split('/').pop() ?? pathOnly;
  const lastDotIndex = lastSegment.lastIndexOf('.');
  const stem =
    lastDotIndex > -1 ? lastSegment.slice(0, lastDotIndex) : lastSegment;

  return new RegExp(`${TRANSPARENT_SUFFIX}(?=__|$)`, 'i').test(stem);
}

interface NormalizeImageVariantPreferenceOptions {
  preferTransparent: boolean;
}

const variantPreferenceKey = (image: MecImage): string =>
  [
    image.swatchMatch ?? '',
    extractCanonicalFilenameStem(image.urlOriginal),
  ].join('|');

export function normalizeImageVariantPreference<T extends MecImage>(
  images: T[],
  options: NormalizeImageVariantPreferenceOptions
): T[] {
  const byCanonical = new Map<
    string,
    { firstSeenIndex: number; selected: T }
  >();

  images.forEach((image, index) => {
    const key = variantPreferenceKey(image);
    const existing = byCanonical.get(key);

    if (!existing) {
      byCanonical.set(key, { firstSeenIndex: index, selected: image });
      return;
    }

    const currentIsTransparent = isTransparentVariant(image.urlOriginal);
    const existingIsTransparent = isTransparentVariant(
      existing.selected.urlOriginal
    );

    const shouldReplace = options.preferTransparent
      ? currentIsTransparent && !existingIsTransparent
      : !currentIsTransparent && existingIsTransparent;

    if (shouldReplace) {
      byCanonical.set(key, {
        firstSeenIndex: existing.firstSeenIndex,
        selected: image,
      });
    }
  });

  return [...byCanonical.values()]
    .sort((a, b) => a.firstSeenIndex - b.firstSeenIndex)
    .map((entry) => entry.selected);
}
